import axios, { AxiosInstance, AxiosError } from 'axios';
import { ApiResponse } from '../types';
import { API_BASE_URL } from '../config/apiConfig';

/*
 * Copyright (c) 2026 Notiosoft SA.
 * All rights reserved.
 *
 * Deliberately a separate axios instance from services/api.ts: platform
 * console requests carry a `platformToken` (operatorId-based JWT), never the
 * tenant `authToken`. Keeping the client, storage keys, and 401 handling
 * fully separate means a platform session and a tenant session can never be
 * confused or clobber one another in localStorage.
 */

export interface PlatformOperator {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
}

export interface MunicipalitySummary {
  id: string;
  name: string;
  code: string;
  email: string | null;
  phone: string | null;
  logoUrl: string | null;
  status: 'trial' | 'active' | 'suspended' | 'cancelled';
  plan: string;
  suspendedAt: string | null;
  suspendedReason: string | null;
  createdAt: string;
  staffCount: number;
  citizenCount: number;
  reportCount: number;
  openReportCount: number;
  resolutionRate: number | null;
  slaBreaches: number;
  lastActiveAt: string | null;
}

export interface MunicipalityDetail extends Omit<MunicipalitySummary, 'staffCount' | 'citizenCount' | 'reportCount' | 'openReportCount' | 'resolutionRate' | 'slaBreaches' | 'lastActiveAt'> {
  address?: string | null;
  website?: string | null;
  staff: Array<{ id: string; first_name: string; last_name: string; email: string; role: string; is_active: boolean; last_login: string | null }>;
  statusBreakdown: Array<{ status: string; n: string }>;
  recentReports: Array<{ id: string; report_number: string; title: string; status: string; created_at: string }>;
}

export interface PlatformAuditEntry {
  id: string;
  action: string;
  details: Record<string, unknown>;
  created_at: string;
  ip_address: string | null;
  operator_name: string | null;
  municipality_name: string | null;
}

export interface BillingPeriod {
  from: string;
  to: string;
}

export interface MunicipalityUsageOverview {
  municipalityId: string;
  municipalityName: string;
  municipalityCode: string;
  plan: string;
  planLabel: string;
  smsUsed: number;
  pushUsed: number;
  smsOverage: number;
  pushOverage: number;
  estimatedCents: number;
}

export interface UsageSummaryEntry {
  eventType: string;
  quantity: number;
  costCents: number;
}

export interface InvoiceLineItem {
  description: string;
  quantity: number;
  unitCostCents: number;
  totalCents: number;
}

export interface Invoice {
  id: string;
  municipality_id: string;
  municipality_name: string | null;
  municipality_code: string | null;
  period_start: string;
  period_end: string;
  status: 'draft' | 'issued' | 'paid' | 'void';
  currency: string;
  subtotal_cents: number;
  line_items: InvoiceLineItem[];
  generated_at: string | null;
  issued_at: string | null;
  paid_at: string | null;
  created_at: string;
}

class PlatformApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: `${API_BASE_URL}/platform-ops`,
      timeout: 10000,
      headers: { 'Content-Type': 'application/json' },
    });

    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('platformToken');
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });

    this.api.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          const url = error.config?.url || '';
          if (!url.includes('/auth/login')) {
            localStorage.removeItem('platformToken');
            localStorage.removeItem('platformOperator');
            window.location.href = '/platform-ops/login';
          }
        }
        return Promise.reject(error);
      }
    );
  }

  private async request<T>(config: any): Promise<ApiResponse<T>> {
    try {
      const response = await this.api.request(config);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const responseMessage =
          (error.response?.data as any)?.error?.message || (error.response?.data as any)?.message;
        return { success: false, error: { message: responseMessage || error.message, statusCode: error.response?.status || 500 } };
      }
      return { success: false, error: { message: 'An unexpected error occurred', statusCode: 500 } };
    }
  }

  login(email: string, password: string) {
    return this.request<{ operator: PlatformOperator; token: string }>({ method: 'POST', url: '/auth/login', data: { email, password } });
  }

  me() {
    return this.request<PlatformOperator>({ method: 'GET', url: '/auth/me' });
  }

  listMunicipalities() {
    return this.request<MunicipalitySummary[]>({ method: 'GET', url: '/municipalities' });
  }

  getMunicipality(id: string) {
    return this.request<MunicipalityDetail>({ method: 'GET', url: `/municipalities/${id}` });
  }

  setMunicipalityStatus(id: string, status: string, reason?: string) {
    return this.request<MunicipalitySummary>({ method: 'PATCH', url: `/municipalities/${id}/status`, data: { status, reason } });
  }

  impersonate(id: string) {
    return this.request<{ token: string; user: any }>({ method: 'POST', url: `/municipalities/${id}/impersonate` });
  }

  auditLog() {
    return this.request<PlatformAuditEntry[]>({ method: 'GET', url: '/audit-log' });
  }

  billingOverview() {
    return this.request<{ period: BillingPeriod; municipalities: MunicipalityUsageOverview[] }>({
      method: 'GET',
      url: '/billing/overview',
    });
  }

  municipalityUsage(id: string) {
    return this.request<{ period: BillingPeriod; usage: UsageSummaryEntry[] }>({ method: 'GET', url: `/municipalities/${id}/usage` });
  }

  municipalityInvoices(id: string) {
    return this.request<Invoice[]>({ method: 'GET', url: `/municipalities/${id}/invoices` });
  }

  generateInvoice(id: string, periodStart?: string, periodEnd?: string) {
    return this.request<Invoice>({ method: 'POST', url: `/municipalities/${id}/invoices`, data: { periodStart, periodEnd } });
  }

  listInvoices() {
    return this.request<Invoice[]>({ method: 'GET', url: '/invoices' });
  }

  getInvoice(id: string) {
    return this.request<Invoice>({ method: 'GET', url: `/invoices/${id}` });
  }

  setInvoiceStatus(id: string, status: Invoice['status']) {
    return this.request<Invoice>({ method: 'PATCH', url: `/invoices/${id}/status`, data: { status } });
  }
}

const platformApiService = new PlatformApiService();
export default platformApiService;
